/**
 * CSS-spec-accurate color matrix composition for GPU postprocessing.
 *
 * CSS filter operations are well-defined linear transforms on [R,G,B,A] color
 * vectors. Each operation can be expressed as a 4x4 matrix + 4-element offset.
 * We compose them left-to-right (matching CSS application order) into a single
 * matrix + offset pair, then apply it in one GPU pass.
 *
 * References:
 *   https://www.w3.org/TR/filter-effects-1/#FilterPrimitiveRepresentation
 *   https://drafts.fxtf.org/filter-effects/#funcdef-filter-brightness
 */

// 4x4 matrix stored column-major (matching GLSL mat4 layout)
// M[col * 4 + row] = element at (row, col)
type Mat4 = Float32Array // length 16
type Vec4 = Float32Array // length 4

export interface ColorMatrix {
  matrix: Float32Array // 4x4, column-major
  offset: Float32Array // 4 element offset (RGBA)
}

// --- Matrix helpers (column-major) ---

function identity(): { m: Mat4; o: Vec4 } {
  const m = new Float32Array(16)
  m[0] = 1; m[5] = 1; m[10] = 1; m[15] = 1
  const o = new Float32Array(4)
  return { m, o }
}

/** Compose two affine transforms: result = B(A(x)) = B.m * A.m, B.m * A.o + B.o */
function compose(
  a: { m: Mat4; o: Vec4 },
  b: { m: Mat4; o: Vec4 },
): { m: Mat4; o: Vec4 } {
  const m = new Float32Array(16)
  // Column-major 4x4 multiply: result[col*4+row] = sum_k B[k*4+row] * A[col*4+k]
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0
      for (let k = 0; k < 4; k++) {
        sum += b.m[k * 4 + row] * a.m[col * 4 + k]
      }
      m[col * 4 + row] = sum
    }
  }
  // Offset: B.m * A.o + B.o
  const o = new Float32Array(4)
  for (let row = 0; row < 4; row++) {
    let sum = 0
    for (let k = 0; k < 4; k++) {
      sum += b.m[k * 4 + row] * a.o[k]
    }
    o[row] = sum + b.o[row]
  }
  return { m, o }
}

// --- CSS filter operation matrices ---
// All per: https://www.w3.org/TR/filter-effects-1/

/** brightness(b): multiply RGB by b */
function brightnessMatrix(b: number): { m: Mat4; o: Vec4 } {
  const { m, o } = identity()
  m[0] = b; m[5] = b; m[10] = b
  return { m, o }
}

/** contrast(c): output = c * (input - 0.5) + 0.5 = c * input + 0.5*(1-c) */
function contrastMatrix(c: number): { m: Mat4; o: Vec4 } {
  const { m, o } = identity()
  m[0] = c; m[5] = c; m[10] = c
  const off = 0.5 * (1 - c)
  o[0] = off; o[1] = off; o[2] = off
  return { m, o }
}

/**
 * saturate(s): uses the exact luminance coefficients from the CSS spec.
 * https://www.w3.org/TR/filter-effects-1/#funcdef-filter-saturate
 */
function saturateMatrix(s: number): { m: Mat4; o: Vec4 } {
  const { m, o } = identity()
  const R = 0.213, G = 0.715, B = 0.072
  // Row 0
  m[0]  = R + (1 - R) * s  // col 0, row 0
  m[4]  = G - G * s         // col 1, row 0
  m[8]  = B - B * s         // col 2, row 0
  // Row 1
  m[1]  = R - R * s         // col 0, row 1
  m[5]  = G + (1 - G) * s   // col 1, row 1
  m[9]  = B - B * s         // col 2, row 1
  // Row 2
  m[2]  = R - R * s         // col 0, row 2
  m[6]  = G - G * s         // col 1, row 2
  m[10] = B + (1 - B) * s   // col 2, row 2
  // Row 3 and col 3 stay identity (alpha pass-through)
  return { m, o }
}

/**
 * sepia(amount): interpolate identity → full sepia by amount (0..1)
 * Full sepia matrix from the CSS spec:
 *   R: 0.393 0.769 0.189
 *   G: 0.349 0.686 0.168
 *   B: 0.272 0.534 0.131
 */
function sepiaMatrix(amount: number): { m: Mat4; o: Vec4 } {
  const { m, o } = identity()
  const a = amount
  // lerp(identity, sepia, amount)
  // Row 0 (R output)
  m[0]  = 1 - a * (1 - 0.393)  // col 0, row 0
  m[4]  = a * 0.769              // col 1, row 0
  m[8]  = a * 0.189              // col 2, row 0
  // Row 1 (G output)
  m[1]  = a * 0.349              // col 0, row 1
  m[5]  = 1 - a * (1 - 0.686)   // col 1, row 1
  m[9]  = a * 0.168              // col 2, row 1
  // Row 2 (B output)
  m[2]  = a * 0.272              // col 0, row 2
  m[6]  = a * 0.534              // col 1, row 2
  m[10] = 1 - a * (1 - 0.131)   // col 2, row 2
  return { m, o }
}

/**
 * hue-rotate(deg): exact matrix from the CSS Filter Effects spec.
 * https://www.w3.org/TR/filter-effects-1/#funcdef-filter-hue-rotate
 *
 * The spec provides the full matrix with hardcoded coefficients.
 * Using the spec's exact values rather than deriving from luminance weights.
 */
function hueRotateMatrix(degrees: number): { m: Mat4; o: Vec4 } {
  const { m, o } = identity()
  const rad = degrees * Math.PI / 180
  const c = Math.cos(rad)
  const s = Math.sin(rad)

  // Row 0 (R output)
  m[0]  = 0.213 + c * 0.787 - s * 0.213
  m[4]  = 0.715 - c * 0.715 - s * 0.715
  m[8]  = 0.072 - c * 0.072 + s * 0.928
  // Row 1 (G output)
  m[1]  = 0.213 - c * 0.213 + s * 0.143
  m[5]  = 0.715 + c * 0.285 + s * 0.140
  m[9]  = 0.072 - c * 0.072 - s * 0.283
  // Row 2 (B output)
  m[2]  = 0.213 - c * 0.213 - s * 0.787
  m[6]  = 0.715 - c * 0.715 + s * 0.715
  m[10] = 0.072 + c * 0.928 + s * 0.072

  return { m, o }
}

// --- Parser ---

interface FilterOp {
  name: string
  value: number
}

function parseFilterOps(filterStr: string): FilterOp[] {
  const ops: FilterOp[] = []
  const re = /(brightness|contrast|saturate|sepia|hue-rotate)\(\s*(-?[\d.]+)(deg)?\s*\)/g
  let match
  while ((match = re.exec(filterStr)) !== null) {
    ops.push({ name: match[1], value: parseFloat(match[2]) })
  }
  return ops
}

function opToMatrix(op: FilterOp): { m: Mat4; o: Vec4 } {
  switch (op.name) {
    case 'brightness': return brightnessMatrix(op.value)
    case 'contrast': return contrastMatrix(op.value)
    case 'saturate': return saturateMatrix(op.value)
    case 'sepia': return sepiaMatrix(op.value)
    case 'hue-rotate': return hueRotateMatrix(op.value)
    default: return identity()
  }
}

/**
 * Parse a CSS filter string and compose all operations into a single 4x4 matrix + offset.
 * Operations are applied left-to-right as per CSS spec.
 */
export function composeCSSFilterMatrix(filterStr: string): ColorMatrix {
  const ops = parseFilterOps(filterStr)
  let result = identity()
  for (const op of ops) {
    result = compose(result, opToMatrix(op))
  }
  return { matrix: result.m, offset: result.o }
}

function composeFilterOps(ops: FilterOp[]): ColorMatrix {
  let result = identity()
  for (const op of ops) {
    result = compose(result, opToMatrix(op))
  }
  return { matrix: result.m, offset: result.o }
}

/**
 * Interpolate a color matrix toward identity by t (0 = identity, 1 = full effect).
 * Lerps each matrix element and offset element independently.
 */
export function lerpColorMatrix(cm: ColorMatrix, t: number): ColorMatrix {
  const id = identity()
  const matrix = new Float32Array(16)
  const offset = new Float32Array(4)
  for (let i = 0; i < 16; i++) {
    matrix[i] = id.m[i] + (cm.matrix[i] - id.m[i]) * t
  }
  for (let i = 0; i < 4; i++) {
    offset[i] = id.o[i] + (cm.offset[i] - id.o[i]) * t
  }
  return { matrix, offset }
}

/**
 * Compose a complete color matrix from a color grade filter string + user adjustments.
 *
 * @param gradeFilterStr - CSS filter string from COLOR_GRADE_FILTERS (or empty)
 * @param gradeIntensity - 0..1 interpolation toward identity for the grade
 * @param brightness - user brightness (CSS scale: 1 = no change)
 * @param contrast - user contrast (CSS scale: 1 = no change)
 * @param saturation - user saturation (CSS scale: 1 = no change)
 * @param hueRotateDeg - user hue rotation in degrees
 */
export function buildColorMatrix(
  gradeFilterStr: string | undefined,
  gradeIntensity: number,
  brightness: number,
  contrast: number,
  saturation: number,
  hueRotateDeg: number,
): ColorMatrix {
  // Start with grade matrix (interpolated by intensity)
  let grade: ColorMatrix
  if (gradeFilterStr) {
    const fullGrade = composeCSSFilterMatrix(gradeFilterStr)
    grade = gradeIntensity >= 1
      ? fullGrade
      : lerpColorMatrix(fullGrade, gradeIntensity)
  } else {
    grade = { matrix: identity().m, offset: identity().o }
  }

  // Build user adjustments as additional CSS filter ops applied after the grade
  const userOps: FilterOp[] = []
  if (brightness !== 1) userOps.push({ name: 'brightness', value: brightness })
  if (contrast !== 1) userOps.push({ name: 'contrast', value: contrast })
  if (saturation !== 1) userOps.push({ name: 'saturate', value: saturation })
  if (hueRotateDeg !== 0) userOps.push({ name: 'hue-rotate', value: hueRotateDeg })

  if (userOps.length === 0) return grade

  const userMatrix = composeFilterOps(userOps)

  // Compose: grade first, then user adjustments
  const combined = compose(
    { m: grade.matrix, o: grade.offset },
    { m: userMatrix.matrix, o: userMatrix.offset },
  )
  return { matrix: combined.m, offset: combined.o }
}

/**
 * Check if a color matrix is effectively identity (no visual change).
 */
export function isIdentityMatrix(cm: ColorMatrix): boolean {
  const eps = 1e-6
  const id = identity()
  for (let i = 0; i < 16; i++) {
    if (Math.abs(cm.matrix[i] - id.m[i]) > eps) return false
  }
  for (let i = 0; i < 4; i++) {
    if (Math.abs(cm.offset[i] - id.o[i]) > eps) return false
  }
  return true
}
